import { Highlight } from '@hungryroot/pho';
import { HowItWorks } from '@hungryroot/plan';
import {
  Paragraph,
  Heading,
  TransparentButton,
  BestPriceComponent,
  Calendar2,
  CartFilled,
  Sparkle,
} from '@hungryroot/ui-kit';
import styles from './YourFutureOrders.module.css';

interface Props {
  subtitle?: string;
  className?: string;
  orderLevelIncentivesValue: number | undefined;
  onClickTermsApply?: () => void;
  cartSource?: keyof typeof cartItems;
}

const MAX_ANNUAL_DELIVERIES = 52;

const cartItems = {
  smartCart: {
    icon: <CartFilled />,
    highlightedText: 'SmartCart™️ builds your cart',
    text: 'based on your ordering habits, budget + tastes',
  },
  cookbook: {
    icon: <CartFilled />,
    text: (
      <>
        <Highlight className={styles.highlight}>
          <Heading variant="H6" as="span">
            Your cart built from your favorites
          </Heading>
        </Highlight>
        <Paragraph>
          Your Cookbook helps us choose recipes and groceries for each cart.
        </Paragraph>
      </>
    ),
  },
};

const items = [
  {
    icon: <Calendar2 />,
    highlightedText: "You're in control",
    text: '– edit, skip, or reschedule your delivery before it ships.',
  },
  {
    icon: <Sparkle />,
    text: (
      <>
        <Paragraph as="span">
          With each delivery, we get smarter—like having a
        </Paragraph>
        <Highlight className={styles.highlight}>
          <Heading variant="H6" as="span">
            personal shopper
          </Heading>
        </Highlight>
        <Paragraph as="span">who gets you</Paragraph>
      </>
    ),
  },
];

function getItems(
  orderLevelIncentivesValue: number | undefined,
  onClickTermsApply?: () => void
) {
  if (orderLevelIncentivesValue) {
    return items.map((item, index) => {
      if (index === 1) {
        const perOrderDiscount = `$${orderLevelIncentivesValue} off`;
        const maxAnnualDiscount = `$${(
          orderLevelIncentivesValue * MAX_ANNUAL_DELIVERIES
        ).toLocaleString()}`;

        return {
          icon: <BestPriceComponent />,
          text: (
            <>
              <Paragraph as="span">You’ll get</Paragraph>
              <Highlight className={styles.highlight}>
                <Heading variant="H6" as="span">
                  {perOrderDiscount}
                </Heading>
              </Highlight>
              <Paragraph as="span">
                future orders – that’s up to {maxAnnualDiscount} in savings per
                year!{' '}
                <TransparentButton onClick={onClickTermsApply}>
                  <span className={styles.terms}>Terms apply</span>
                </TransparentButton>
              </Paragraph>
            </>
          ),
        };
      }
      return item;
    });
  }
  return items;
}

export function YourFutureOrders({
  subtitle,
  className,
  orderLevelIncentivesValue,
  onClickTermsApply,
  cartSource = 'smartCart',
}: Props) {
  return (
    <HowItWorks
      title="Your future orders"
      headingVariant="H2"
      titleClassName={styles.title}
      subtitle={<Paragraph className={styles.subtitle}>{subtitle}</Paragraph>}
      items={[
        cartItems[cartSource],
        ...getItems(orderLevelIncentivesValue, onClickTermsApply),
      ]}
      className={className}
    />
  );
}
